import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader, CheckCircle, XCircle } from "lucide-react";
import api from "../lib/api";

export default function AcceptInviteView() {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("invite.missing_token"));
      setLoading(false);
      return;
    }
    api.get(`/users/invitation/${token}`)
      .then(res => setInvitation(res.data))
      .catch(e => setError(e?.response?.data?.detail ?? t("invite.not_found")))
      .finally(() => setLoading(false));
  }, [token, t]);

  const submit = async () => {
    if (password !== confirm) {
      setError(t("invite.password_mismatch"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/users/accept-invite", { token, password });
      setSuccess(true);
      setTimeout(() => { window.location.href = "/"; }, 2000);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? t("invite.accept_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <Loader className="w-6 h-6 animate-spin text-brand-600" />
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-surface-200 shadow-soft p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-danger-bg rounded-2xl flex items-center justify-center mx-auto mb-3">
            <XCircle className="w-6 h-6 text-danger" />
          </div>
          <p className="text-[15px] font-semibold text-ink-900 mb-1">{t("invite.invalid_title")}</p>
          <p className="text-[13px] text-ink-500">{error}</p>
          <a href="/" className="inline-block mt-6 text-[13px] text-brand-600 hover:underline">{t("invite.go_to_app")}</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-surface-200 shadow-soft p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-success-bg rounded-2xl flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-6 h-6 text-success" />
          </div>
          <p className="text-[15px] font-semibold text-ink-900 mb-1">{t("invite.success_title")}</p>
          <p className="text-[13px] text-ink-500">{t("invite.success_sub")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-surface-200 shadow-soft p-8 max-w-md w-full">
        <h1 className="text-[18px] font-semibold text-ink-900 mb-1">{t("invite.title")}</h1>
        <p className="text-[13px] text-ink-500 mb-6">
          {t("invite.body_pre")} <strong>{invitation?.company_name}</strong> {t("invite.body_as")} <strong>{invitation?.role}</strong>{t("invite.body_post")}
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-[12px] font-medium text-ink-500 mb-1.5 block">{t("invite.email")}</label>
            <input value={invitation?.email || ""} readOnly
              className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-[13px] text-ink-500" />
          </div>
          <div>
            <label className="text-[12px] font-medium text-ink-500 mb-1.5 block">{t("invite.name")}</label>
            <input value={invitation?.full_name || ""} readOnly
              className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-[13px] text-ink-500" />
          </div>
          <div>
            <label className="text-[12px] font-medium text-ink-500 mb-1.5 block">{t("invite.password_label")}</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-[13px] text-ink-900 focus:bg-white transition-all" />
          </div>
          <div>
            <label className="text-[12px] font-medium text-ink-500 mb-1.5 block">{t("invite.confirm_label")}</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-[13px] text-ink-900 focus:bg-white transition-all" />
          </div>

          {error && (
            <div className="rounded-xl bg-danger-bg border border-danger-border text-danger text-[13px] p-3">{error}</div>
          )}

          <button onClick={submit} disabled={submitting || !password || !confirm}
            className="w-full px-4 py-3 rounded-xl text-[14px] font-semibold bg-brand-600 text-white hover:bg-brand-500 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
            {submitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
            {t("invite.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
